import React, { useContext, useEffect, useReducer, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'

const BreadcrumbContent = React.createContext()

export const useBreadcrumbContext = () => useContext(BreadcrumbContent)

export const useRouteStack = () => {
  const history = useHistory()

  const breadcrumbContext = useBreadcrumbContext()

  const backRoute = level => {
    const item = breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]

    if (item) {
      history.replace(item.url)
    } else {
      history.goBack()
    }
  }

  const getStackLevel = level => {
    return breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]
  }

  const replaceStackLevel = (level, append) => {
    const item = breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]
    if (item) {
      history.replace(`${item.url}/${append}`)
    }
  }

  return { backRoute, getStackLevel, replaceStackLevel }
}

const useBreadcrumbReducer = () => {
  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'add':
        return [...state, action.payload]
      case 'update':
        let index = state.findIndex(i => i.url === action.payload.url)
        if (index !== -1) {
          let newState = [...state]
          newState[index] = action.payload
          return newState
        } else {
          return state
        }
      case 'remove':
        return state.filter(i => i.url !== action.payload)
      default:
        return state
    }
  }, [])

  const add = (url, text, Icon, block) => dispatch({ type: 'add', payload: { url, text, Icon, block } })
  const update = (url, text, Icon, block) => dispatch({ type: 'update', payload: { url, text, Icon, block } })
  const remove = url => dispatch({ type: 'remove', payload: url })

  return [state, add, update, remove]
}

const APP_NAME = 'EducationXR'

// item.text is normally a string, but a handful of pages hand it a value that
// isn't ready yet (a record name before its GET resolves is ''/undefined) or,
// in principle, something that isn't a string/number at all -- rendering that
// as a crumb is fine (React just renders it), but stringifying it for
// document.title is not, so anything else is treated as absent rather than
// coerced (which would print "[object Object]" for e.g. a React element).
const breadcrumbTextToString = text => {
  if (typeof text !== 'string' && typeof text !== 'number') {
    return ''
  }

  return String(text).trim()
}

// Title = the last two non-empty breadcrumb texts, most specific (deepest,
// i.e. last pushed) first, joined by " · ", then " · EducationXR" -- so every
// history entry / tab / bookmark reads something more useful than the bare
// app name. Exported for unit testing independent of the provider/effect.
export const titleFromStack = stack => {
  const texts = (stack || [])
    .map(item => breadcrumbTextToString(item && item.text))
    .filter(text => text.length > 0)

  const mostSpecificFirst = texts.slice(-2).reverse()

  return [...mostSpecificFirst, APP_NAME].join(' · ')
}

export const BreadcrumbProvider = ({ children }) => {
  const [stack, add, update, remove] = useBreadcrumbReducer()

  useEffect(
    () => {
      document.title = titleFromStack(stack)
    },
    [stack]
  )

  return (
    <BreadcrumbContent.Provider value={{ stack, add, update, remove }}>
      {children}
    </BreadcrumbContent.Provider>
  )
}

// The trail is assembled from whichever BreadcrumbViews are MOUNTED, and nesting
// normally falls out of React Router v5's non-exact path matching: at
// /admin/sso/connections/7 the <Route path="/admin/sso/connections"> still
// matches, so the list's BreadcrumbView mounts (registering "PROVIDERS") while
// rendering no children, and the edit page registers itself underneath it.
//
// A page whose path is a SIBLING rather than a descendant -- /admin/sso/jobs
// next to /admin/sso/connections -- has no ancestor route to mount, so it used to
// show a bare one-item trail even though it is only reachable through its
// section. `parent` names that section entry explicitly: it is registered just
// before this view's own, so the trail reads "PROVIDERS > JOBS" and the first
// crumb navigates back to the section.
//
// Shape: { url, text, Icon, block } -- the same four values `add` takes.
export const BreadcrumbView = ({ children, Icon, text, block, parent, onMatch, onPath }) => {
  const { url, isExact } = useRouteMatch()

  const [search, updateSearch] = useState()

  const history = useHistory()
  const location = useLocation()

  const breadcrumbContext = useBreadcrumbContext()

  // A parent whose url equals this view's own would duplicate the crumb -- that
  // happens if a section root is handed its own entry as its parent.
  const parentUrl = parent && parent.url !== url ? parent.url : null

  useEffect(
    () => {
      if (parentUrl) {
        breadcrumbContext.add(parentUrl, parent.text, parent.Icon, parent.block)
      }

      breadcrumbContext.add(url, text, Icon, block)

      return () => {
        breadcrumbContext.remove(url)
        if (parentUrl) {
          breadcrumbContext.remove(parentUrl)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location]
  )

  useEffect(
    () => {
      breadcrumbContext.update(url, text, Icon, block)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, Icon, block]
  )

  useEffect(
    () => {
      if (isExact) {
        onMatch && onMatch()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isExact]
  )

  useEffect(
    () => {
      onPath && location.pathname.includes(url) && onPath()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  )

  useEffect(
    () => {
      if (isExact) {
        updateSearch(document.location.search)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document.location.search]
  )

  useEffect(
    () => {
      if (isExact && search) {
        history.replace({ search: search })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isExact]
  )

  return (
    <React.Fragment>
      {isExact &&
        <React.Fragment>
          {children}
        </React.Fragment>}
    </React.Fragment>
  )
}
